import React from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, Calculator, CheckCircle, DollarSign, 
  Clock, Shield, Phone, ArrowRight, Star
} from 'lucide-react';

const FinancingPage = () => {
  const financingOptions = [
    {
      title: '0% Interest for 12 Months',
      description: 'No interest charges for qualified customers on approved purchases',
      features: ['No Interest for 12 Months', 'Quick Approval Process', 'Online Application', 'Same-Day Funding'],
      highlight: 'Most Popular'
    },
    {
      title: 'Extended Payment Plans',
      description: 'Flexible payment options up to 7 years for major installations',
      features: ['Up to 84 Months', 'Low Monthly Payments', 'Fixed Interest Rates', 'No Prepayment Penalties'],
      highlight: 'Best Value'
    },
    {
      title: 'Cash Discount Program',
      description: 'Save money with our cash payment discount program',
      features: ['Immediate Savings', 'No Credit Check Required', 'Same-Day Service', 'Upfront Pricing'],
      highlight: 'Instant Savings'
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Quick Approval',
      description: 'Get approved in minutes with our streamlined application process'
    },
    {
      icon: Shield,
      title: 'Secure & Safe',
      description: 'Your information is protected with bank-level security encryption'
    },
    {
      icon: Calculator,
      title: 'Payment Calculator',
      description: 'Calculate your monthly payments before you apply'
    },
    {
      icon: CheckCircle,
      title: 'No Hidden Fees',
      description: 'Transparent pricing with no surprise charges or hidden fees'
    }
  ];

  const qualifyingServices = [
    'Water Heater Installation',
    'HVAC System Replacement',
    'Boiler Installation',
    'Emergency Plumbing Repairs',
    'Sewer Line Replacement',
    'Oil-to-Gas Conversions',
    'Complete System Upgrades',
    'Energy Efficiency Improvements'
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-green-600 to-blue-700 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1551135049-8a33b5883817?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxmaW5hbmNpbmclMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzUwMTc5OTc0fDA&ixlib=rb-4.1.0&q=85"
            alt="Financing Consultation"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center space-x-2 bg-green-600/90 text-white px-6 py-3 rounded-full font-semibold backdrop-blur-sm"
                whileHover={{ scale: 1.05 }}
              >
                <DollarSign className="h-5 w-5" />
                <span>Flexible Payment Options</span>
              </motion.div>

  <h1 className="text-5xl lg:text-6xl font-bold leading-normal">
  Affordable
  <span className="bg-gradient-to-r from-green-300 to-cyan-300 bg-clip-text text-transparent block pb-2">
    Financing
  </span>
  Solutions
</h1>

              <p className="text-xl text-green-100 leading-relaxed">
                Don't let budget constraints stop you from getting the plumbing, heating, or air conditioning services you need. Our flexible financing options make it easy to afford quality work.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  className="inline-flex items-center space-x-2 bg-white text-green-700 px-8 py-4 rounded-full font-bold text-lg shadow-2xl hover:shadow-white/25 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Apply Now</span>
                  <ArrowRight className="h-6 w-6" />
                </motion.button>
                
                <motion.a
                  href="tel:+1-973-862-0606"
                  className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold border-2 border-white/20 hover:bg-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Phone className="h-5 w-5" />
                  <span>Call for Details</span>
                </motion.a>
              </div>
            </motion.div>

            <motion.div
              className="lg:justify-self-end"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="bg-white/95 backdrop-blur-lg p-8 rounded-3xl shadow-2xl max-w-md">
                <div className="text-center space-y-6">
                  <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-2xl w-fit mx-auto">
                    <CreditCard className="h-12 w-12 text-white" />
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Quick Application</h3>
                    <p className="text-gray-600">Get approved in minutes and start your project today</p>
                  </div>

                  <div className="space-y-4 text-left">
                    {['No impact to credit score for pre-qualification', 'Instant approval decisions', 'Same-day funding available'].map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  <motion.button
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Apply for Financing
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Financing Options */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Choose Your Financing Option
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer multiple financing solutions to fit your budget and needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {financingOptions.map((option, index) => (
              <motion.div
                key={index}
                className={`bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 relative ${
                  index === 0 ? 'border-green-500 scale-105' : 'border-gray-100'
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                {option.highlight && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                      {option.highlight}
                    </div>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{option.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{option.description}</p>
                </div>
                
                <div className="space-y-4 mb-8">
                  {option.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <motion.button
                  className={`w-full py-3 px-6 rounded-2xl font-semibold transition-all duration-300 ${
                    index === 0 
                      ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Learn More
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Our Financing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We make it easy and affordable to get the services you need
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="text-center p-6 rounded-2xl hover:bg-green-50 transition-all duration-300 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-2xl w-fit mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <benefit.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualifying Services */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
                Services That Qualify for Financing
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Most of our services qualify for financing options. From emergency repairs to complete system installations, we make it affordable.
              </p>
              
              <motion.a
                href="tel:+1-973-862-0606"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Phone className="h-5 w-5" />
                <span>Discuss Your Project</span>
              </motion.a>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {qualifyingServices.map((service, index) => (
                <motion.div
                  key={index}
                  className="bg-white p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-3"
                  whileHover={{ scale: 1.02 }}
                >
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                  <span className="font-medium text-gray-800">{service}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
            <section className="relative py-20 text-white overflow-hidden">
              {/* Background Image with Gradient Overlay */}
              <div className="absolute inset-0 z-0">
                <img 
                  src="/images/HomePage/CTAHomePage.webp"
                  alt="Construction and Roadwork Scene"
                  className="w-full h-full object-cover object-top opacity-12"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#2462E9]/90 to-[#1E40AF]/90"></div>
              </div>
      
              <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <motion.div
                  className="space-y-8"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                                             {/* First Text Container */}
                    <div className="mb-6">
                      <h3 className="text-[15px] font-bold text-white mb-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-full px-4 py-2 inline-block">
                        Plumbing & HVAC estimate in Morris County
                      </h3>
                    </div>
      
                   {/* Second Text Container */}
                   <div className="mb-6">
                     <h2 className="text-[64px]s..2. font-bold text-white mb-4">
                       Get Your Free <span className="text-[#45C1FF]">Plumbing & HVAC</span>Estimate Today
                     </h2>
                   </div>
      
                   {/* Third Text Container */}
                   <div className="mb-6">
                     <p className="text-[24px] font-bold text-white">
                       Need immediate help or planning a new installation?
                     </p>
                   </div>
      
                   {/* Fourth Text Container */}
                   <div className="mb-8">
                     <p className="text-[20px] font-normal text-white max-w-4xl mx-auto leading-relaxed">
                       Call Mount Olive Plumbing now or request your free, no-obligation estimate. Our licensed team provides expert plumbing, heating, and air conditioning solutions day or night.
                     </p>
                   </div>
                  
                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-6 justify-center">
                                   <motion.a
                       href="tel:+1-973-862-0606"
                       className="inline-flex items-center space-x-3 bg-white text-blue-700 px-8 py-4 rounded-full font-bold text-lg shadow-2xl hover:shadow-white/25 transition-all duration-300"
                       whileHover={{ scale: 1.05 }}
                       whileTap={{ scale: 0.95 }}
                     >
                       <Phone className="h-6 w-6" />
                       <span>Call: (973) 862-0606</span>
                     </motion.a>
                    
                    {/* <motion.button
                      className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold border-2 border-white/20 hover:bg-white/20 transition-all duration-300"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>Get Free Estimate</span>
                      <ArrowRight className="h-5 w-5" />
                    </motion.button> */}
                  </div>
                </motion.div>
              </div>
            </section>
    </div>
  );
};

export default FinancingPage;